import { produce } from 'immer';

/**
 * A simple, safe JSON Patch applicator for Zustand state.
 * It uses Immer to avoid direct state mutation.
 * It only supports 'add', 'replace', and 'remove' operations.
 *
 * @param state The current state object.
 * @param op The JSON Patch operation to apply.
 * @returns The new state after applying the patch.
 */
export function applyJsonPatch<T>(state: T, op: { op: 'add' | 'replace' | 'remove'; path: string; value?: any }): T {
  return produce(state, (draft: any) => {
    // Split path into segments, removing the initial empty string from '/a/b'
    const pathSegments = op.path.split('/').slice(1);
    if (pathSegments.length === 0) {
      console.error('Patch operation must have a path.', op);
      return;
    }

    let current = draft;
    const lastSegment = pathSegments.pop()!;

    // Navigate to the parent object/array
    for (const segment of pathSegments) {
      if (typeof current !== 'object' || current === null) {
        console.error('Invalid path for patch operation:', op.path);
        return;
      }
      current = current[segment];
    }
    
    if (typeof current !== 'object' || current === null) {
        console.error('Invalid path for patch operation:', op.path);
        return;
    }

    switch (op.op) {
      case 'add':
      case 'replace':
        current[lastSegment] = op.value;
        break;
      case 'remove':
        if (Array.isArray(current)) {
          const index = parseInt(lastSegment, 10);
          if (!isNaN(index)) {
            current.splice(index, 1);
          }
        } else {
          delete current[lastSegment];
        }
        break;
      default:
        console.warn('Unsupported patch operation:', op.op);
    }
  });
}